import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, BookOpen, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * What the coach read when it opened a whole document.
 *
 * Same job as `VaultSourcesWidget`, one step further: reading returns a
 * continuous stretch of a named document rather than the passages that matched
 * a query, so the thing worth showing is *which* document and *how much of it*.
 * A summary built from part 1 of 4 is a summary of a quarter of the document,
 * and the part counter is the only place that is visible.
 */

/** Longest stretch of the read text shown before clipping. */
const MAX_PREVIEW_CHARS = 400;

export interface VaultReadOutput {
  status: "ok" | "no_documents" | "not_found" | "error";
  title: string;
  part: number;
  partCount: number;
  text: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asPositiveInt(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 1
    ? Math.trunc(value)
    : fallback;
}

/**
 * Reads the `intelligence_read_document` payload defensively; anything
 * unrecognised yields `null` so the chat renders nothing rather than an empty
 * box. An `ok` payload naming no document is treated as `not_found` — there is
 * nothing to attribute the answer to either way.
 */
export function parseVaultReadOutput(output: unknown): VaultReadOutput | null {
  const record = asRecord(output);
  if (!record) return null;

  const status = record.status;
  if (status === "error" || record.isError === true) {
    return { status: "error", title: "", part: 1, partCount: 0, text: "" };
  }
  if (status !== "ok" && status !== "no_documents" && status !== "not_found") {
    return null;
  }

  const title = typeof record.document === "string" ? record.document.trim() : "";
  if (status !== "ok") {
    return { status, title: "", part: 1, partCount: 0, text: "" };
  }
  if (!title) {
    return { status: "not_found", title: "", part: 1, partCount: 0, text: "" };
  }

  return {
    status: "ok",
    title,
    part: asPositiveInt(record.part, 1),
    partCount: asPositiveInt(record.partCount, 1),
    text: typeof record.text === "string" ? record.text.trim() : "",
  };
}

export function VaultReadWidget({ output }: { output: unknown }) {
  const { t } = useTranslation("intelligence");
  const [open, setOpen] = useState(false);
  const parsed = parseVaultReadOutput(output);
  if (!parsed) return null;

  if (parsed.status === "error") {
    return (
      <div className="mt-1 flex items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {t("tool.vault.failed")}
      </div>
    );
  }

  if (parsed.status !== "ok") {
    return (
      <div className="mt-1 rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs leading-tight text-muted-foreground">
        {parsed.status === "no_documents"
          ? t("tool.vault.noDocuments")
          : t("tool.vault.notFound")}
      </div>
    );
  }

  const preview =
    parsed.text.length > MAX_PREVIEW_CHARS
      ? `${parsed.text.slice(0, MAX_PREVIEW_CHARS).trimEnd()}…`
      : parsed.text;
  const expandable = preview.length > 0;

  const label = (
    <>
      <BookOpen className="h-3 w-3 shrink-0 text-muted-foreground/60" />
      <span className="truncate font-medium text-foreground/90">
        {parsed.title}
      </span>
      {parsed.partCount > 1 && (
        <span className="shrink-0 text-muted-foreground">
          {t("tool.vault.part", {
            part: parsed.part,
            partCount: parsed.partCount,
          })}
        </span>
      )}
    </>
  );

  return (
    <div className="mt-1 max-w-sm rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs leading-tight text-muted-foreground">
      <div className="mb-1 font-medium text-foreground/90">
        {t("tool.vault.readTitle")}
      </div>
      {expandable ? (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex w-full items-center gap-1.5 text-left transition-colors hover:text-foreground"
        >
          <ChevronRight
            className={cn(
              "h-3 w-3 shrink-0 text-muted-foreground/60 transition-transform",
              open && "rotate-90",
            )}
          />
          {label}
        </button>
      ) : (
        <div className="flex items-center gap-1.5 pl-[18px]">{label}</div>
      )}
      {open && (
        <p className="mt-1 whitespace-pre-wrap border-l-2 border-border pl-2 text-[11px] leading-snug text-muted-foreground">
          {preview}
        </p>
      )}
    </div>
  );
}
